import { useEffect, useState } from "react";
import { IconArrowLeft, IconDeviceFloppy, IconPlus, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Button,
  Card,
  Checkbox,
  Heading,
  IconButton,
  Input,
  ListaControlesSkeleton,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
} from "@/components/ui";
import { qk } from "@/lib/query-keys";
import { toastErrorApi, toastExito } from "@/lib/toast";
import {
  guardarConfiguracion,
  obtenerConfiguracion,
  type ConfigColumnaCalc,
  type ConfigCompleta,
} from "../services/admin-reporteria.api";
import { useRolesReporteria, useUsuariosReporteria } from "../hooks/use-admin-reporteria";
import { ConfiguracionEditorSkeleton } from "./ReporteriaAdminSkeletons";
import { TablasColumnasEditor } from "./TablasColumnasEditor";

function vacia(): ConfigCompleta {
  return {
    id: null,
    nombre: "",
    descripcion: null,
    tipo: "rol",
    activo: true,
    tablas: [],
    roles: [],
    usuarios: [],
    columnasCalculadas: [],
  };
}

export function ConfiguracionEditor({ idConfig, onSalir }: { idConfig: number | null; onSalir: () => void }) {
  const cargar = useQuery({
    queryKey: qk.repAdminConfig(idConfig ?? 0),
    queryFn: () => obtenerConfiguracion(idConfig!),
    enabled: idConfig != null,
  });
  const roles = useRolesReporteria();
  const usuarios = useUsuariosReporteria();

  const [cfg, setCfg] = useState<ConfigCompleta>(vacia);
  const [listo, setListo] = useState(idConfig == null);

  useEffect(() => {
    if (idConfig != null && cargar.data && !listo) {
      setCfg(cargar.data);
      setListo(true);
    }
  }, [idConfig, cargar.data, listo]);

  const guardar = useMutation({
    mutationFn: () => guardarConfiguracion(cfg),
    onSuccess: () => {
      toastExito({ title: idConfig ? "Configuración actualizada" : "Configuración creada" });
      onSalir();
    },
    onError: toastErrorApi,
  });

  if (!listo) return <ConfiguracionEditorSkeleton />;

  const set = (patch: Partial<ConfigCompleta>) => setCfg((c) => ({ ...c, ...patch }));

  const toggleRol = (rol: string) =>
    set({ roles: cfg.roles.includes(rol) ? cfg.roles.filter((r) => r !== rol) : [...cfg.roles, rol] });
  const toggleUsuario = (id: string) =>
    set({ usuarios: cfg.usuarios.includes(id) ? cfg.usuarios.filter((u) => u !== id) : [...cfg.usuarios, id] });

  const setCalc = (i: number, patch: Partial<ConfigColumnaCalc>) =>
    set({ columnasCalculadas: cfg.columnasCalculadas.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) });

  return (
    <Stack gap="md">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <IconButton aria-label="Volver" variant="subtle" onClick={onSalir}>
            <IconArrowLeft size={18} />
          </IconButton>
          <Heading order={3}>{idConfig ? "Editar configuración" : "Nueva configuración"}</Heading>
        </div>
        <Button leftSection={<IconDeviceFloppy size={16} />} loading={guardar.isPending} onClick={() => guardar.mutate()}>
          Guardar
        </Button>
      </div>

      {/* Cabecera */}
      <Card className="p-4">
        <Stack gap="sm">
          <Input label="Nombre" value={cfg.nombre} onChange={(e) => set({ nombre: e.currentTarget.value })} required />
          <Textarea
            label="Descripción (opcional)"
            rows={2}
            value={cfg.descripcion ?? ""}
            onChange={(e) => set({ descripcion: e.currentTarget.value || null })}
          />
          <div className="flex flex-wrap items-center gap-6">
            <div className="w-52">
              <Select
                label="Tipo de asignación"
                value={cfg.tipo}
                onValueChange={(v) => set({ tipo: v })}
                options={[
                  { value: "rol", label: "Por rol" },
                  { value: "usuario", label: "Por usuario" },
                ]}
              />
            </div>
            <Switch checked={cfg.activo} onCheckedChange={(v) => set({ activo: v })} label="Activa" />
          </div>
        </Stack>
      </Card>

      {/* Tablas y columnas */}
      <Card className="p-4">
        <Heading order={5} className="mb-2">
          Tablas y columnas habilitadas
        </Heading>
        <TablasColumnasEditor tablas={cfg.tablas} onChange={(tablas) => set({ tablas })} />
      </Card>

      {/* Columnas calculadas de fábrica */}
      <Card className="p-4">
        <Heading order={5} className="mb-2">
          Columnas calculadas de fábrica
        </Heading>
        <Text size="xs" c="dimmed" className="mb-2">
          Fórmulas NCalc que verán todos los usuarios de esta configuración (referencia columnas por su etiqueta: [Etiqueta]).
        </Text>
        <div className="space-y-2">
          {cfg.columnasCalculadas.map((c, i) => (
            <div key={i} className="space-y-1 rounded-md border border-border p-2">
              <div className="flex items-center gap-1.5">
                <Input
                  className="w-40"
                  placeholder="Nombre"
                  value={c.nombre}
                  onChange={(e) => setCalc(i, { nombre: e.currentTarget.value })}
                />
                <div className="w-40">
                  <Select
                    size="sm"
                    value={c.tipo}
                    onValueChange={(v) => setCalc(i, { tipo: v })}
                    options={[
                      { value: "fila", label: "Por fila" },
                      { value: "agregado", label: "Agregado (total)" },
                    ]}
                  />
                </div>
                <IconButton
                  aria-label="Quitar"
                  variant="subtle"
                  onClick={() => set({ columnasCalculadas: cfg.columnasCalculadas.filter((_, idx) => idx !== i) })}
                >
                  <IconTrash size={16} />
                </IconButton>
              </div>
              <Textarea
                rows={2}
                placeholder="Fórmula · ej. [Subtotal] * 1.16"
                value={c.formula}
                onChange={(e) => setCalc(i, { formula: e.currentTarget.value })}
              />
            </div>
          ))}
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={() =>
              set({ columnasCalculadas: [...cfg.columnasCalculadas, { nombre: "", formula: "", tipo: "fila", formato: null }] })
            }
          >
            Agregar columna calculada
          </Button>
        </div>
      </Card>

      {/* Asignación */}
      <Card className="p-4">
        <Heading order={5} className="mb-2">
          Asignación
        </Heading>
        {cfg.tipo === "rol" ? (
          <>
            <Text size="xs" c="dimmed" className="mb-2">
              Roles con acceso (los usuarios marcados abajo quedan EXCLUIDOS).
            </Text>
            <div className="mb-3 flex flex-wrap gap-x-6 gap-y-1">
              {(roles.data ?? []).map((r) => (
                <Checkbox
                  key={r}
                  id={`rep-rol-${r}`}
                  checked={cfg.roles.includes(r)}
                  onCheckedChange={() => toggleRol(r)}
                  label={r}
                />
              ))}
            </div>
          </>
        ) : (
          <Text size="xs" c="dimmed" className="mb-2">
            Usuarios con acceso (solo los marcados abajo).
          </Text>
        )}

        <Text size="sm" fw={600} className="mb-1">
          {cfg.tipo === "rol" ? "Usuarios excluidos" : "Usuarios incluidos"}
        </Text>
        {usuarios.isLoading ? (
          <ListaControlesSkeleton filas={5} control="checkbox" conBorde etiqueta="Cargando usuarios…" />
        ) : (
          <div className="max-h-56 space-y-1 overflow-y-auto rounded-md border border-border p-2">
            {(usuarios.data ?? []).map((u) => (
              <Checkbox
                key={u.id}
                id={`rep-usr-${u.id}`}
                checked={cfg.usuarios.includes(u.id)}
                onCheckedChange={() => toggleUsuario(u.id)}
                label={
                  <span className="text-sm">
                    {u.nombre ?? u.email} <span className="text-muted-foreground">· {u.email}</span>
                  </span>
                }
              />
            ))}
          </div>
        )}
      </Card>
    </Stack>
  );
}
